import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getAllCurrenciesStart } from '../../../../store/redux-slices/currencies'
import { getAllSuperAdminTransactionsStart, getBankingTransactionsStart, updateTransactionStatusStart, updateCryptoTransactionStatusStart } from '../../../../store/redux-slices/superAdminTransactions'
import { convertToEasternISO, formatDateYMD, getDateDaysAgo } from '../../../../utils/dateFormatter'
import { AdminsRoutes } from '../../../../routes'
import { getLoginToken } from '../../../../utils/storageUtils'
import useDidMountEffect from '../../../../utils/useDidMountEffect'
import { statusType } from '../constants'
import { getDriversListStart } from '../../../../store/redux-slices/transactionDriversSlice'
import { getFileRequest } from '../../../../utils/axios'
import { toast } from '../../../../components/Toast'
import { getDateRangeByFilter, defaultDateFilter } from '../../../../utils/helpers';


const useTransactionBList = ({ isUserDetail, transactionType }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userId } = useParams()

  const [selectedClient, setSelectedClient] = useState('')
  const [selectedPortal, setSelectedPortal] = useState('')
  const [selectedCurrency, setSelectedCurrency] = useState('')
  const [selectedAction, setSelectedAction] = useState('')
  const [limit, setLimit] = useState(15)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchString, setSearchString] = useState('')
  const [status, setStatus] = useState('')
  const [newStatus, setNewStatus] = useState(0)
  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [showCryptoStatusModal, setShowCryptoStatusModal] = useState(false)

  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [agentId, setAgentId] = useState(0)
  const { allCurrencies } = useSelector((state) => state.currencies)
  const { loading: superAdminTransactionsLoading, transactions, loadingCrypto } = useSelector((state) => state.superAdminTransactions)
  const { loading: driversListLoading, driversList } = useSelector((state) => state.transactionDrivers)


  const totalPages = Math.ceil(transactions?.count / limit)
  const totalCount = transactions?.count

  const isInitialRender = useDidMountEffect()
  const [selectedFilter, setSelectedFilter] = useState(defaultDateFilter);

  const selectedCrypto = useRef(null);


  const [state, setState] = useState(() => {
    const { startDate, endDate } = getDateRangeByFilter(defaultDateFilter);
    return [
      {
        startDate,
        endDate,
        key: "selection"
      }
    ];
  });

  const prepareDatesForAPI = (startDate, endDate) => {
    const isSameDay = startDate?.toDateString() === endDate?.toDateString()

    if (isSameDay) {
      return {
        startDate: convertToEasternISO(startDate, 0, 0, 0, 0),
        endDate: convertToEasternISO(startDate, 23, 59, 59, 999)
      }
    } else {
      return {
        startDate: convertToEasternISO(startDate, 0, 0, 0, 0),
        endDate: convertToEasternISO(endDate, 23, 59, 59, 999)
      }
    }
  }

  useEffect(() => {
    if (!transactionType) {
      dispatch(getAllCurrenciesStart({ limit: '', pageNo: '' }))
    } else {
      dispatch(getDriversListStart())
    }
  }, [])

  const onDeposit = () => {
    navigate(AdminsRoutes.Deposit)
  }

  const openStatusModal = (transaction) => {
    setSelectedTransaction(transaction)
    setNewStatus(transaction.status || 0)
    setAgentId(transaction.agentId || 0)
    setShowStatusModal(true)
  }

  const openCryptoStatusModal = (transaction) => {
    setSelectedTransaction(transaction)
    setNewStatus(transaction.status || 0)
    setAgentId(transaction.agentId || 0)
    setShowCryptoStatusModal(true)
  }

  const closeStatusModal = () => {
    setShowStatusModal(false)
    setSelectedTransaction(null)
  }

  const closeCryptoStatusModal = () => {
    setShowCryptoStatusModal(false)
    setSelectedTransaction(null)
  }

  const handleStatusChange = (e) => {
    setNewStatus(e.target.value)
  }

  const handleAgentIdChange = (e) => {
    setAgentId(e.target.value)
  }

  const updateTransactionStatus = () => {
    setUpdatingStatus(true)

    // Get original dates from state
    const originalStartDate = state[0]?.startDate
    const originalEndDate = state[0]?.endDate

    const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

    dispatch(updateTransactionStatusStart({
      transactionId: selectedTransaction.transactionId,
      actioneeEmail: selectedTransaction.actioneeEmail,
      status: statusType.find((s) => s.value === Number(newStatus))?.key,
      amount: selectedTransaction.amount,
      agentId: parseInt(agentId),
      onSuccess: () => {
        closeStatusModal()
        setUpdatingStatus(false)

        dispatch(getBankingTransactionsStart({
          limit,
          pageNo: page,
          searchString,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          status,
          paymentMethod,
          transactionType
        }))
      },
      onError: () => {
        closeStatusModal()
        setUpdatingStatus(false)
      }
    }))
  }


  const updateCryptoTransactionStatus = ({ id }) => {
    setUpdatingStatus(true)

    // Get original dates from state
    const originalStartDate = state[0]?.startDate
    const originalEndDate = state[0]?.endDate

    const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

    dispatch(updateCryptoTransactionStatusStart({
      id,

      onSuccess: () => {
        closeCryptoStatusModal()
        setUpdatingStatus(false)
        dispatch(getBankingTransactionsStart({
          limit,
          pageNo: page,
          searchString,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          status,
          paymentMethod,
          transactionType
        }))
      },
      onError: () => {
        closeStatusModal()
        setUpdatingStatus(false)
      }
    }))
  }

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (selectedPaymentProvider.length > 2) {
        // Get original dates from state
        const originalStartDate = state[0]?.startDate
        const originalEndDate = state[0]?.endDate

        const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

        dispatch(getAllSuperAdminTransactionsStart({
          limit,
          pageNo: page,
          search,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          currencyId: selectedCurrency,
          transactionType: selectedAction,
          tenantId: selectedPortal,
          adminId: selectedClient,
          paymentProvider: selectedPaymentProvider,
          isUserDetail,
          userId,
          status
        }))
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
  }, [selectedPaymentProvider])

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchString.length > 2) {
        // Get original dates from state
        const originalStartDate = state[0]?.startDate
        const originalEndDate = state[0]?.endDate

        const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

        dispatch(getBankingTransactionsStart({
          limit,
          pageNo: page,
          searchString,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          status,
          paymentMethod,
          transactionType
        }))
      }
    }, 1000)

    return () => clearTimeout(delayDebounceFn)
  }, [searchString])

  useEffect(() => {
    if (!isInitialRender) {
      // Get original dates from state
      const originalStartDate = state[0]?.startDate
      const originalEndDate = state[0]?.endDate

      const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

      if (!transactionType) {
        dispatch(getAllSuperAdminTransactionsStart({
          limit,
          pageNo: page,
          search,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          currencyId: selectedCurrency,
          transactionType: selectedAction,
          tenantId: selectedPortal,
          adminId: selectedClient,
          paymentProvider: selectedPaymentProvider,
          isUserDetail,
          userId,
          status
        }))
      } else {
        dispatch(getBankingTransactionsStart({
          limit,
          pageNo: page,
          searchString,
          startDate: apiDates.startDate,
          endDate: apiDates.endDate,
          status,
          paymentMethod,
          transactionType
        }))
      }
    }
  }, [page])

  const onResetDateFiltered = () => {
    setSelectedFilter(defaultDateFilter)
    const { startDate, endDate } = getDateRangeByFilter(defaultDateFilter);
    setState([
      {
        startDate,
        endDate,
        key: "selection"
      }
    ]);
  }

  useEffect(() => {
    setPage(1)

    // Get original dates from state
    const originalStartDate = state[0]?.startDate
    const originalEndDate = state[0]?.endDate

    const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

    if (transactionType === 'deposit' || transactionType === 'withdrawal') {
      dispatch(getBankingTransactionsStart({
        limit,
        pageNo: 1,
        searchString,
        startDate: apiDates.startDate,
        endDate: apiDates.endDate,
        status,
        paymentMethod,
        transactionType
      }))
    } else {
      dispatch(getAllSuperAdminTransactionsStart({
        limit,
        pageNo: 1,
        search,
        startDate: apiDates.startDate,
        endDate: apiDates.endDate,
        currencyId: selectedCurrency,
        transactionType: selectedAction,
        tenantId: selectedPortal,
        adminId: selectedClient,
        paymentProvider: selectedPaymentProvider,
        isUserDetail,
        userId,
        status
      }))
    }
  }, [limit, selectedPortal, selectedCurrency, selectedAction, state, selectedClient, search, status, searchString, paymentMethod])

  // const getCsvDownloadUrl = (player) => {
  //   // Get original dates from state
  //   const originalStartDate = state[0]?.startDate
  //   const originalEndDate = state[0]?.endDate

  //   const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate)

  //   if (player) {
  //     return `${process.env.REACT_APP_API_URL}/api/admin/transactions?csvDownload=true&limit=${limit}&pageNo=${page}&actioneeType=${search}&startDate=${formatDateYMD(new Date(apiDates.startDate))}&endDate=${formatDateYMD(new Date(apiDates.endDate))}&currencyCode=${selectedCurrency}&transactionType=${selectedAction}&tenantId=${selectedPortal}&adminId=${selectedClient}&paymentProvider=${selectedPaymentProvider}&token=${getLoginToken()}&userId=${userId}`
  //   } else {
  //     let url = `${process.env.REACT_APP_API_URL}/api/admin/payments/transactions?csvDownload=true&limit=${limit}&pageNo=${page}`

  //     if (searchString) {
  //       url += `&search=${searchString}`
  //     }

  //     if (newStatus) {
  //       url += `&status=${statusType.find(status => status.value === newStatus).value}`
  //     }

  //     url += `&startDate=${apiDates.startDate}&endDate=${apiDates.endDate}`

  //     if (paymentMethod) {
  //       url += `&paymentMethod=${paymentMethod}`
  //     }

  //     return url
  //   }
  // }

  const getCsvDownloadUrl = (player) => {
    const originalStartDate = state[0]?.startDate;
    const originalEndDate = state[0]?.endDate;

    const apiDates = prepareDatesForAPI(originalStartDate, originalEndDate);

    const baseUrl = player
      ? `${process.env.REACT_APP_API_URL}/api/admin/transactions`
      : `${process.env.REACT_APP_API_URL}/api/admin/payments/transactions`;

    const params = new URLSearchParams({
      csvDownload: true,
      transactionType: transactionType || "",
      searchString,
      // startDate: originalStartDate,
      // endDate: originalEndDate,

    });

    if (player) {
      if (apiDates.startDate) params.append("startDate", apiDates.startDate);
      if (apiDates.endDate) params.append("endDate", apiDates.endDate);
      if (search) params.append("actioneeType", search);
      if (selectedCurrency) params.append("currencyCode", selectedCurrency);
      if (selectedAction) params.append("transactionType", selectedAction);
      if (selectedPortal) params.append("tenantId", selectedPortal);
      if (selectedClient) params.append("adminId", selectedClient);
      if (selectedPaymentProvider) params.append("paymentProvider", selectedPaymentProvider);
      if (userId) params.append("userId", userId);
      params.append("token", getLoginToken());
    } else {
      if (searchString) params.append("search", searchString);
      if (newStatus) {
        const statusValue = statusType.find(status => status.value === newStatus)?.value;
        if (statusValue) params.append("status", statusValue);
      }
      if (apiDates.startDate) params.append("startDate", apiDates.startDate);
      if (apiDates.endDate) params.append("endDate", apiDates.endDate);
      if (paymentMethod) params.append("paymentMethod", paymentMethod);
      if (status) params.append("status", status);
    }

    return `${baseUrl}?${params.toString()}`;
  };


  const handleDownload = async (format = 'csv', transactionType = "player_transaction") => {
    console.log("enterrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrrr", transactionType)
    try {
      const response = await getFileRequest(getCsvDownloadUrl());

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = URL.createObjectURL(blob);

      const element = document.createElement('a');
      element.href = downloadUrl;
      element.download = `${transactionType}s_${format}_${new Date().toISOString().split('T')[0]}.csv`;
      element.style.display = 'none';

      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      URL.revokeObjectURL(downloadUrl);

      toast(`${format.toUpperCase()} downloaded successfully`, 'success');
    } catch (error) {
      console.error('Download error:', error);
      toast('Error downloading CSV', 'error');
    }
  };

  return {
    allCurrencies,
    setSelectedCurrency,
    setSearch,
    setLimit,
    setPage,
    totalPages,
    limit,
    page,
    setSelectedAction,
    navigate,
    selectedCurrency,
    selectedAction,
    search,
    state,
    setState,
    transactions,
    loading: superAdminTransactionsLoading || driversListLoading,
    onDeposit,
    selectedClient,
    setSelectedClient,
    selectedPortal,
    setSelectedPortal,
    setSelectedPaymentProvider,
    selectedPaymentProvider,
    getCsvDownloadUrl,
    status,
    setStatus,
    setSearchString,
    setPaymentMethod,
    paymentMethod,
    searchString,
    showStatusModal,
    setShowStatusModal,
    selectedTransaction,
    setSelectedTransaction,
    openStatusModal,
    closeStatusModal,
    updateTransactionStatus,
    updatingStatus,
    newStatus,
    handleStatusChange,
    driversList,
    agentId,
    handleAgentIdChange,
    handleDownload,
    setSelectedFilter,
    selectedFilter,
    onResetDateFiltered,
    openCryptoStatusModal,
    showCryptoStatusModal,
    setShowCryptoStatusModal,
    updateCryptoTransactionStatus,
    loadingCrypto,
    selectedCrypto,
    totalCount
  }
}

export default useTransactionBList